<template>
  <div class="d-flex">
    <div v-if="hasPreviousVersion" :class="formVersionClass">
      <label>{{ $i18n.interpolate($i18n.t('previous_form_data_version')) }}: {{ version - 1 }}</label>
      <FormDataVersionView
        ref="previousVersionView"
        :url="formUrl"
        :id-form="previousFormId"
        @compare="compareForms($event)"
        :version="{ previous: true, number: version - 1 }"
      >
      </FormDataVersionView>
    </div>

    <div :class="formVersionClass">
      <label>{{ $i18n.interpolate($i18n.t('this_form_data_version')) }}: {{ version }}</label>
      <FormDataVersionView
        ref="currentVersionView"
        v-bind="$props"
        @compare="compareForms($event)"
        :version="{ previous: false, number: version }"
      >
      </FormDataVersionView>
    </div>
  </div>
</template>

<script>
import formStore from '../../store/modules/form/index'
import { mapState } from 'vuex'
import Vue from 'vue'
import urlModule from 'url'
import { pickBy, find, forIn, intersection, intersectionWith, head } from 'lodash'
import querystring from 'querystring'
import Loader from '../Loader'
import FormDataVersionView from './FormDataVersionView'
import EventBus from '../../utils/event-bus'

/**
 * @class FormDataVersion
 *
 * @description This component renders the comparison of Form dataversions
 */
export default {
  name: 'FormDataVersion',
  instanceStore: {
    name: 'formStore',
    module: formStore,
  },
  props: {
    idForm: {
      type: String,
      default: '',
    },
    level: Number,
    externalParams: {
      type: Object,
      default: () => ({}),
    },
    groupAutoCollapse: {
      type: Boolean,
      default: false,
    },
    subformView: {
      type: String,
      default: 'Default',
    },
    usernameHidden: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      mainTable: null,
      formUrl: '',
      isLoading: true,
      hiddenFormNumber: null,
      idFormDataVersion: null,
      version: 1,
      current: {},
      previous: {},
      previousFormId: '',
      formVersions: {},
    }
  },
  created() {
    const urlParsed = urlModule.parse(this.url)
    const params = querystring.parse(urlParsed.query)
    this.idFormDataVersion = params.id || false
    if (!this.idFormDataVersion) {
      console.warn('no form data version')
      this.isLoading = false
      return
    }
    if (this.isLoading) this.init()
  },
  mounted() {
    EventBus.$on('subFormStoreRedraw', () => {
      this.$nextTick(() => {
        this.triggerMarkChanges()
      })
    })
  },
  beforeDestroy() {
    EventBus.$off('subFormStoreRedraw')
    this.$store.unregisterModule(this.$storeNamespace)
  },
  destroyed() {
    this.$store.unregisterModule(this.$storeNamespace)
  },
  methods: {
    triggerMarkChanges() {
      if (this.$refs.previousVersionView && this.$refs.currentVersionView) {
        this.$refs.previousVersionView.markChanges(this.changes.previous)
        this.$refs.currentVersionView.markChanges(this.changes.current)
      }
    },
    async init() {
      const { formStructure, version, idMaintable } = await this.getDataVersion()
      this.current = formStructure.forms
      this.previous = await this.getPreviousVersion(idMaintable, version)
      this.previousFormId = this.previous.id
      this.version = Number(version)
      this.isLoading = false
    },
    async getDataVersion() {
      const parameters = {
        filter: JSON.stringify({
          where: {
            id: this.idFormDataVersion,
          },
        }),
      }
      const response = await Vue.prototype.$client
        .execute({
          operationId: 'FormDataVersions_findOne',
          parameters,
        })
        .catch(() => {
          console.error('not valid form data version')
          return { formStructure: {} }
        })
      return response.body
    },
    async getPreviousVersion(idMaintable, version) {
      if (version === 1) return {}
      const parameters = {
        filter: JSON.stringify({
          where: {
            idMaintable: idMaintable,
            version: version - 1,
          },
        }),
      }
      const response = await Vue.prototype.$client
        .execute({
          operationId: 'FormDataVersions_findOne',
          parameters,
        })
        .catch(() => {
          console.error('no former versions found')
        })
      return response.body
    },
    addFieldDifference(key, itemClass) {
      this.formVersions.previous[key].class = itemClass
      this.formVersions.current[key].class = itemClass
    },
    sfFieldModified(obj, otherObj) {
      return obj.id === otherObj.id && obj.value !== otherObj.value
    },
    areItemsMatchIn(arrayOne, arrayTwo) {
      return arrayOne.length === arrayTwo.length && arrayOne.length === intersection(arrayOne, arrayTwo).length
    },
    compareForms({ previous, values }) {
      const emptyValue = [null, '']
      if (previous) {
        this.formVersions.previous = values
      } else {
        this.formVersions.current = values
      }
      if (Object.keys(this.formVersions).length === 2) {
        // a formokon levo subformokon levo expressionok jol ujrarajzoljak a komponenst, ki kell varni
        this.$nextTick(() => {
          const previousForm = this.formVersions.previous
          const currentForm = this.formVersions.current
          let fieldState = ''
          forIn(previousForm, (value, key) => {
            if (currentForm.hasOwnProperty(key)) {
              if (Array.isArray(currentForm[key].value) && Array.isArray(previousForm[key].value)) {
                const prevArray = previousForm[key].value
                const thisArray = currentForm[key].value
                if (currentForm[key].type === 'subform') {
                  let modified = intersectionWith(thisArray, prevArray, this.sfFieldModified).map((field) => field.id)
                  for (let field of prevArray) {
                    if (!find(thisArray, { id: field.id })) field.class = 'removed'
                  }
                  for (let field of thisArray) {
                    if (!find(prevArray, { id: field.id })) field.class = 'added'
                  }
                  for (let field of thisArray) {
                    if (modified.includes(field.id)) field.class = 'modified'
                  }
                  for (let field of prevArray) {
                    if (modified.includes(field.id)) field.class = 'modified'
                  }
                } else if (prevArray && prevArray.length !== thisArray && thisArray.length) {
                  fieldState = 'modified'
                } else {
                  if (thisArray.length === 0) {
                    fieldState = 'equals'
                  } else {
                    if (Array.isArray(head(prevArray)) && Array.isArray(head(thisArray))) {
                      for (let item in prevArray) {
                        previousForm[key].type = 'tags'
                        currentForm[key].type = 'tags'
                        fieldState = this.areItemsMatchIn(prevArray[item], thisArray[item]) ? 'equals' : 'modified'
                        if (fieldState === 'modified') {
                          break
                        }
                      }
                    } else {
                      fieldState = this.areItemsMatchIn(prevArray, thisArray) ? 'equals' : 'modified'
                    }
                  }
                }
              } else if (emptyValue.includes(currentForm[key].value) && emptyValue.includes(previousForm[key].value)) {
                fieldState = 'equals'
              } else if (['time', 'date', 'datetime', 'datetime-local'].includes(currentForm[key].type)) {
                fieldState =
                  new Date(previousForm[key].value).getTime() === new Date(currentForm[key].value).getTime() ||
                  previousForm[key].value == currentForm[key].value
                    ? 'equals'
                    : 'modified'
              } else if (currentForm[key].type === 'radiobuttonlist') {
                fieldState = head(previousForm[key].value) == head(currentForm[key].value) ? 'equals' : 'modified'
              } else if (currentForm[key].type === 'checkbox') {
                fieldState =
                  Boolean(previousForm[key].value) === Boolean(currentForm[key].value) ? 'equals' : 'modified'
              } else if (currentForm[key].type === 'number') {
                fieldState = Number(previousForm[key].value) === Number(currentForm[key].value) ? 'equals' : 'modified'
              } else if (['range', 'rating'].includes(currentForm[key].type)) {
                fieldState = +previousForm[key].value === +currentForm[key].value ? 'equals' : 'modified'
              } else {
                fieldState = currentForm[key].value === previousForm[key].value ? 'equals' : 'modified'
              }
              this.addFieldDifference(key, fieldState)
            } else {
              previousForm[key].class = 'removed'
            }
          })
          const newFields = pickBy(currentForm, (field) => !field.class)
          forIn(newFields, (value, key) => {
            currentForm[key].class = 'added'
          })
          this.$nextTick(() => this.triggerMarkChanges())
        })
      }
    },
  },
  computed: {
    ...mapState('popup', ['popups']),
    hasPreviousVersion() {
      return this.version > 1
    },
    formVersionClass() {
      return this.hasPreviousVersion ? 'col-12 col-xl-6 p-2' : 'col-12 p-2'
    },
    changes() {
      return {
        current: pickBy(this.formVersions.current, (field) => {
          return field.class !== 'equals' || field.type === 'subform'
        }),
        previous: pickBy(this.formVersions.previous, (field) => {
          return field.class !== 'equals' || field.type === 'subform'
        }),
      }
    },
  },
  components: {
    Loader,
    FormDataVersionView,
  },
}
</script>
<style lang="scss" scoped>
@import '../../assets/sass/components/_dynform.scss';
</style>
